import type { OnChanges } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { Validators } from '@angular/forms';
import { castArray } from 'lodash';
import type { Observable } from 'rxjs';
import { Subject, takeUntil } from 'rxjs';
import type { OnyxMaybeArray } from '../../../interfaces';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';

@Component({
  selector: 'onyx-form-group',
  imports: [OnyxErrorComponent, OnyxInputLabelComponent],
  templateUrl: './onyx-form-group.component.html',
  styleUrl: './onyx-form-group.component.scss',
  host: { '[style.width]': `width()` },
})
export class OnyxFormGroupComponent implements OnChanges {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly variant = input<'label' | 'title'>('label');
  public readonly label = input('');
  public readonly hint = input<OnyxTooltipContext>();
  public readonly width = input('100%');
  public readonly controls = input<OnyxMaybeArray<AbstractControl>>();
  public readonly gap = input(6);
  public readonly errors = input(true);
  public readonly errorsAlign = input<'left' | 'right'>('left');

  public readonly labelClick = output<void>();

  private required_ = signal(false);
  protected get required() {
    return this.required_.asReadonly();
  }

  private disabled_ = signal(false);
  protected get disabled() {
    return this.disabled_.asReadonly();
  }

  private next$ = new Subject<void>();

  public ngOnChanges(): void {
    effect(
      () => {
        this.next$.next();
        this.update();

        for (const formControl of castArray(this.controls() ?? [])) {
          const statusChanges$ = (
            formControl.statusChanges as Observable<any> | undefined
          )?.pipe(takeUntil(this.next$), takeUntilDestroyed(this.destroyRef));
          const valueChanges$ = (
            formControl.valueChanges as Observable<any> | undefined
          )?.pipe(takeUntil(this.next$), takeUntilDestroyed(this.destroyRef));

          statusChanges$?.subscribe(() => this.update());
          valueChanges$?.subscribe(() => this.update());
        }
      },
      { injector: this.injector },
    );
  }

  private update(): void {
    const controls = this.controls();
    if (controls == null) return;

    let required = false;
    let disabled = true;

    for (const control of castArray(controls)) {
      if (
        control.hasValidator(Validators.required) &&
        !control.hasValidator(Validators.requiredTrue)
      ) {
        required = true;
      }
      if (!control.disabled) disabled = false;
    }

    this.required_.set(required);
    this.disabled_.set(disabled);
  }
}
